import React, { useEffect, useState } from 'react';
import { authService } from '../services/auth.service';

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  useEffect(() => {
    // Check if this is an OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (errorParam) {
      // User cancelled or error occurred at Azure AD
      if (errorParam === 'access_denied') {
        setError('Login was cancelled. Please try again to access the application.');
      } else {
        setError(errorDescription || 'Authentication failed. Please try again.');
      }
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && state) {
      handleOAuthCallback(code, state);
    }
  }, []);

  const handleOAuthCallback = async (code: string, state: string) => {
    setIsProcessingCallback(true);
    setError(null);

    try {
      const redirectUrl = await authService.handleCallback(code, state);
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (onLoginSuccess) {
        onLoginSuccess();
      }
      
      // Redirect to deep link if provided
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (err: any) {
      console.error('OAuth callback error:', err);
      
      if (err.response?.status === 503) {
        setError('Authentication service is temporarily unavailable. Please try again later.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Authentication failed. Please try again.');
      }
      
      // Clean up URL on error
      window.history.replaceState({}, document.title, window.location.pathname);
    } finally {
      setIsProcessingCallback(false);
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get the intended destination from query params if present
      const urlParams = new URLSearchParams(window.location.search);
      const redirectUrl = urlParams.get('redirect') || undefined;
      
      await authService.login(redirectUrl);
      // Login will redirect to Azure AD, so we won't reach here
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response?.status === 503) {
        setError('Authentication service is temporarily unavailable. Please try again later.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to initiate login. Please try again.');
      }
      
      setIsLoading(false);
    }
  };

  if (isProcessingCallback) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ie-pale">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-ie-cyan mb-4"></div>
            <h2 className="font-display text-2xl font-bold text-ie-navy">
              Completing sign in...
            </h2>
            <p className="mt-2 text-sm text-ie-slate">
              Please wait while we verify your credentials.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ie-pale py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white border border-ie-rule rounded-tl-sheet rounded-tr-lg rounded-b-lg p-8 sm:p-10">
        <div>
          <img src="/invest-europe-logo-wordmark.png" alt="Invest Europe" className="h-14 w-auto mb-6" />
          <h2 className="font-display text-3xl font-bold text-ie-navy">
            Sign in to your account
          </h2>
          <p className="mt-2 text-base text-ie-slate">
            Use your Azure Active Directory credentials to continue
          </p>
        </div>

        {error && (
          <div role="alert" className="rounded-lg border-l-4 border-ie-alert bg-ie-pale p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-ie-alert"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-ie-alert">
                  Authentication Error
                </h3>
                <div className="mt-2 text-sm text-ie-navy">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          <button
            onClick={handleLogin}
            disabled={isLoading}
            className={`group relative w-full flex items-center justify-center h-12 px-4 border border-ie-navy rounded-pill font-condensed text-sm font-medium uppercase tracking-wide transition-colors duration-300 ${
              isLoading
                ? 'bg-ie-pale text-ie-slate cursor-not-allowed'
                : 'bg-ie-navy text-white hover:bg-ie-sky hover:text-ie-navy'
            }`}
          >
            {isLoading ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-ie-navy mr-2"></span>
                Redirecting to Microsoft...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" />
                </svg>
                Sign in with Microsoft
              </>
            )}
          </button>
        </div>

        <div className="text-center text-xs text-ie-slate">
          <p>
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </p>
          <p className="mt-2">
            Need help? Contact your system administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
